import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountTreeOutlined } from '@mui/icons-material';
import PageHeader from 'shared/components/page-header/page-header';

import SettingTabs from 'features/settings/settings-tabs';
import { useEmergencyAlarmSettingsContextProvider } from '../context/emergency-alarm-settings-provider';
import EmergencyAlarmSettingDetails from './emergency-alarm-settings-details/emergency-alarm-setting-details.component';

import './emergency-alarm-settings.scss';

import OutlineButton from 'shared/components/buttons/outline-button/outline-button';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import Table from 'shared/components/table/table';
import RowItem from 'shared/components/table/types/row-item';

import EmergencyAlarmSetting from '../domain/models/emergency-alarm-setting';
import AlarmSettingIndicator from './alarm-setting-indicator';

const EmergencyAlarmSettings = (): ReactElement => {
  const { t } = useTranslation('emergencyAlarm');
  const { organisationEmergencyAlarmSettings, defaultTemplateSetting, emergencyAlarmSettingsDetailsHook, emergencyAlarmSettingsIsLoading } =
    useEmergencyAlarmSettingsContextProvider();

  const columns: ColumnDefinition<EmergencyAlarmSetting, keyof EmergencyAlarmSetting>[] = [
    {
      key: 'name',
      label: t('table.columns.location'),
    },
    {
      key: 'isDefaultTemplateSetting',
      label: t('table.columns.settings'),
      renderCustomContentProvider: (settings) => <AlarmSettingIndicator isDefault={settings.isDefaultTemplateSetting} />,
      disableSort: true,
    },
  ];

  const hasNoOrganisationId =
    emergencyAlarmSettingsDetailsHook.currentSelectedEmergencyAlarmSettings &&
    !emergencyAlarmSettingsDetailsHook.currentSelectedEmergencyAlarmSettings.organisationUnitId;

  const selectedItem = organisationEmergencyAlarmSettings?.find(
    (setting) => setting.organisationUnitId === emergencyAlarmSettingsDetailsHook.currentSelectedEmergencyAlarmSettings?.organisationUnitId,
  );

  return (
    <>
      <PageHeader title={t('pageHeaderTitle')} navigationComponent={<SettingTabs />} />
      <div className="emergency-alarm-settings-container">
        <div className="left-content">
          <OutlineButton
            icon={<AccountTreeOutlined />}
            label={t('organisationSettings')}
            isSelected={hasNoOrganisationId}
            onClick={() => {
              emergencyAlarmSettingsDetailsHook.selectSettingDetails(defaultTemplateSetting);
            }}
          ></OutlineButton>
          {emergencyAlarmSettingsIsLoading && <LoadingIndicator />}
          {defaultTemplateSetting?.emergencyAlarmEnabled &&
            organisationEmergencyAlarmSettings &&
            organisationEmergencyAlarmSettings?.length > 0 && (
              <Table
                data={organisationEmergencyAlarmSettings.map<RowItem<EmergencyAlarmSetting>>((setting) => ({ data: setting }))}
                columns={columns}
                currentItem={selectedItem}
                onItemClick={(setting: EmergencyAlarmSetting) => emergencyAlarmSettingsDetailsHook.selectSettingDetails(setting)}
                rowIdentifier={(item: EmergencyAlarmSetting) => item.id}
                useBuiltInSorter={true}
              />
            )}
        </div>
        <EmergencyAlarmSettingDetails />
      </div>
    </>
  );
};

export default EmergencyAlarmSettings;
